import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom"; // ← Renders child routes
import { apiRecords } from "../lib/workerApi";
import { useUser } from "../contexts/UserContext";
import { useDeviceType } from "../contexts/DeviceTypeContext";
import { pb } from "../lib/pocketbaseConfig";
import { PromoSide, recordPromoClick } from "../utils/promoAttribution";
import "./Layout.css";

const WEEKLY_PICKS_BANNER_STORAGE_KEY = "weekly-picks-banner-collapsed";
const WEEKLY_PICKS_BANNER_VISIBLE = false;

type PromoConfig = {
  active?: boolean | number;
};

type PromoFeature = { label: string; icon: "bell" | "store" | "list" | "savings" };

const DEFAULT_PROMOS: Record<PromoSide, { title: string; description: string; buttonText: string; features: PromoFeature[] }> = {
  left: {
    title: "Не пропускай добрата оферта!",
    description: "Създай профил и се възползвай от повече възможности.",
    buttonText: "Започни сега",
    features: [
      { label: "Известия за нови любими продукти", icon: "bell" },
      { label: "Известия за нови брошури от любимите магазини", icon: "store" },
      { label: "Лесно споделяне на списъци за пазаруване", icon: "list" },
      { label: "Следи колко спестяваш с всяка покупка", icon: "savings" },
    ],
  },
  right: {
    title: "Не пропускай добрата оферта!",
    description: "Създай профил и се възползвай от повече възможности.",
    buttonText: "Създай профил",
    features: [
      { label: "Известия за любимите ти продукти", icon: "bell" },
      { label: "Нови брошури от любимите ти магазини", icon: "store" },
      { label: "Споделени списъци за пазаруване", icon: "list" },
      { label: "Следи колко си спестил", icon: "savings" },
    ],
  },
};

function PromoFeatureIcon({ name }: { name: PromoFeature["icon"] }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
  };

  switch (name) {
    case "bell":
      return <svg {...common}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></svg>;
    case "store":
      return <svg {...common}><path d="M4 10v10h16V10M3 10l2-6h14l2 6" /><path d="M3 10a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" /><path d="M9 20v-6h6v6" /></svg>;
    case "list":
      return <svg {...common}><path d="M8 6h12M8 12h8M8 18h6" /><path d="M4 6h.01M4 12h.01M4 18h.01" /><path d="m16 16 4 4m0-4-4 4" /></svg>;
    case "savings":
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7v10m3-7c0-1.2-1.2-2-3-2s-3 .8-3 2 1.2 2 3 2 3 .8 3 2-1.2 2-3 2-3-.8-3-2" /></svg>;
  }
}

function isGuestVisitor(currentUser: ReturnType<typeof useUser>["currentUser"]): boolean {
  if (!currentUser?.isGuest) return false;
  if (!pb.authStore.isValid) return true;
  return pb.authStore.model?.collectionName === "guest_users_auth";
}

function PromoSideBanner({ side, config }: { side: PromoSide; config: PromoConfig }) {
  const { currentUser } = useUser();
  const deviceType = useDeviceType();
  const defaults = DEFAULT_PROMOS[side];
  const isActive = config.active === true || config.active === 1;
  if (!isActive || !currentUser || !isGuestVisitor(currentUser)) return null;

  return (
    <Link
      to="/profile?signup=1"
      className={`signup-promo signup-promo--${side}`}
      aria-label="Създай профил във VsichkiBroshuri"
      onClick={() => recordPromoClick(side, currentUser.$id, currentUser.isGuest, deviceType)}
    >
      <span className="signup-promo__eyebrow">ПАЗАРУВАЙ ПО-УМНО</span>
      <h2>{defaults.title}</h2>
      <p>{defaults.description}</p>
      <ul>
        {defaults.features.map((feature) => (
          <li key={feature.icon}>
            <PromoFeatureIcon name={feature.icon} />
            <span>{feature.label}</span>
          </li>
        ))}
      </ul>
      <span className="signup-promo__cta">
        {defaults.buttonText}
      </span>
      <small>Бързо • Лесно • Удобно</small>
    </Link>
  );
}

const Layout: React.FC = () => {
  const location = useLocation();
  const { currentUser, loading: userLoading } = useUser();
  const [isBannerCollapsed, setIsBannerCollapsed] = useState(
    () => window.localStorage.getItem(WEEKLY_PICKS_BANNER_STORAGE_KEY) !== "false",
  );
  const [promoConfigs, setPromoConfigs] = useState<Partial<Record<PromoSide, PromoConfig>>>({});
  const isHomePage = location.pathname === "/";
  const showLeftPromo = !userLoading && isGuestVisitor(currentUser) && Boolean(promoConfigs.left?.active);
  const showRightPromo = !userLoading && isGuestVisitor(currentUser) && Boolean(promoConfigs.right?.active);

  useEffect(() => {
    let active = true;
    Promise.all(((["left", "right"] as PromoSide[]).map(async (side) => {
      try {
        const record = await apiRecords("internal").getFirstListItem(`name = "${side}PromoBar"`);
        return [side, { active: record.active === true || record.active === 1 }] as const;
      } catch {
        return [side, { active: false }] as const;
      }
    }))).then((entries) => {
      if (active) setPromoConfigs(Object.fromEntries(entries) as Record<PromoSide, PromoConfig>);
    });
    return () => { active = false; };
  }, []);

  const collapseBanner = () => {
    window.localStorage.setItem(WEEKLY_PICKS_BANNER_STORAGE_KEY, "true");
    setIsBannerCollapsed(true);
  };

  const expandBanner = () => {
    window.localStorage.setItem(WEEKLY_PICKS_BANNER_STORAGE_KEY, "false");
    setIsBannerCollapsed(false);
  };

  return (
    <div
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column", marginTop: "25px" }}
    >
      {WEEKLY_PICKS_BANNER_VISIBLE && isHomePage && !isBannerCollapsed && (
        <aside className="desktop-catalog-banner" aria-label="Седмични предложения">
          <button
            type="button"
            className="desktop-catalog-banner__close"
            onClick={collapseBanner}
            aria-label="Затвори седмичните предложения"
          />
          <Link to="/aktualni/promoci/katalozi/" className="desktop-catalog-banner__cta">
            Виж офертите
          </Link>
        </aside>
      )}
      {WEEKLY_PICKS_BANNER_VISIBLE && isHomePage && isBannerCollapsed && (
        <button
          type="button"
          className="desktop-catalog-banner-tab"
          onClick={expandBanner}
          aria-label="Покажи седмичните предложения"
        >
          Седмични предложения
        </button>
      )}
      <div className="site-layout-grid">
        {showLeftPromo && <PromoSideBanner side="left" config={promoConfigs.left || { active: false }} />}
        <main className="site-layout-content" style={{ flex: 1 }}>
          <Outlet /> {/* ← Renders Home/StorePage/etc. */}
        </main>
        {showRightPromo && <PromoSideBanner side="right" config={promoConfigs.right || { active: false }} />}
      </div>
    </div>
  );
};

export default Layout;
